import React, { FC, useCallback, useState } from 'react';

import { getTruncatedNameWithSuffix } from '@eika-infrastruktur/format';
import { LinkButton } from '@eika/button';
import { WarningCard, SummaryCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { IconLink, Link, LinkType } from '@eika/link';
import { Loader } from '@eika/loader';
import { RichIconTypes } from '@eika/rich-icon';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { useConfig } from '../../defaultConfig';
import { transferToSelector, useAccounts, useIsCloseable } from '../../fetchHooks/useAccount';
import type { AccountId, DebitCardDetailEndpoint } from '../../generated-types';
import useTranslation, { formatJsx } from '../../hooks/useTranslation';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { EikaModal } from '../EikaModal';
import CloseAccountForm from './CloseAccountForm';
import createDeleteAccountRequest from './createDeleteAccountRequest';

import './CloseAccount.scss';

interface Props {
    show: boolean;
    accountId: AccountId;
    cards?: DebitCardDetailEndpoint[];
    setShow: React.Dispatch<boolean>;
}

const CloseAccountModal: FC<Props> = ({ show, setShow, accountId, cards }) => {
    const { closeAccount } = useTranslation();
    const { allAccountsLink, contactPageLink } = useConfig();
    const { signingStatus, dispatch, startSigning } = useSigningContext(createDeleteAccountRequest);
    const { data: isCloseable, isLoading: closableLoading } = useIsCloseable(accountId, show);
    const { data: accounts } = useAccounts({ select: transferToSelector, enabled: show });
    const activeCards = cards?.filter((card) => !card.inactive);
    const eligibleTransferAccounts = accounts?.filter((account) => account.accountId.value !== accountId.value);

    const [transferTo, setTransferTo] = useState<string>('');

    const closeModal = useCallback(() => {
        if (signingStatus === 'COMPLETED') {
            window.location.href = allAccountsLink.url;
        } else {
            setTransferTo('');
            dispatch({ type: Actions.RESET });
            setShow(false);
        }
    }, [allAccountsLink.url, dispatch, setShow, signingStatus]);

    const onSubmit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            startSigning({ accountId, transferTo: { value: transferTo } });
        },
        [accountId, startSigning, transferTo],
    );

    return (
        <EikaModal
            id="closeAccount"
            className="close-account-modal"
            open={show}
            setOpen={setShow}
            onClose={closeModal}
            contentShouldStretch
        >
            {closableLoading ? (
                <section className="close-account__loading-container">
                    <Loader />
                </section>
            ) : (
                <>
                    {!!activeCards?.length && (
                        <>
                            <h2>{closeAccount.closeCardTitle}</h2>
                            <SummaryCard icon={RichIconTypes.HAAND_HEIHEI} className="close-account__close-cards">
                                <p className="close-account__close-card-text">
                                    {formatJsx(
                                        closeAccount.closeCardText,
                                        <span className="bold">
                                            {activeCards.length} {closeAccount.card}
                                        </span>,
                                    )}
                                </p>
                                {activeCards.length > 1 && (
                                    <p className="close-account__close-card-description">
                                        {closeAccount.closeCardDescription}
                                    </p>
                                )}
                                <ol className="close-account__card-list">
                                    {activeCards.map((card) => (
                                        <li key={card.name} className="close-account__card-list-item">
                                            <a className="close-account__card-link" href={card.url}>
                                                <span className="close-account__card-list-item__card-name">
                                                    {getTruncatedNameWithSuffix(
                                                        card.name,
                                                        closeAccount.cardOwnerSuffix,
                                                    )}
                                                </span>
                                                <IconLink
                                                    className="close-account__form__card-list-item__arrow"
                                                    href={card.url}
                                                    icon={IconTypes.PIL_HOEYRE}
                                                />
                                            </a>
                                        </li>
                                    ))}
                                </ol>
                            </SummaryCard>
                            <LinkButton className="close-account__close-card-cancel-btn" onClick={() => setShow(false)}>
                                {closeAccount.cancel}
                            </LinkButton>
                        </>
                    )}
                    {eligibleTransferAccounts && eligibleTransferAccounts?.length > 0 && isCloseable ? (
                        <CloseAccountForm
                            onSubmit={onSubmit}
                            setTransferTo={setTransferTo}
                            eligibleTransferAccounts={eligibleTransferAccounts}
                            transferTo={transferTo}
                            onClose={closeModal}
                        />
                    ) : (
                        <>
                            {!activeCards?.length && (
                                <WarningCard title={closeAccount.cannotDelete} isModal>
                                    <p>{closeAccount.contactUs}</p>
                                    {contactPageLink && (
                                        <div className="close-account__button-container">
                                            <Link
                                                href={contactPageLink.url}
                                                linkType={LinkType.BUTTON_STANDARD_POSITIVE}
                                            >
                                                {closeAccount.contactUsButton}
                                            </Link>
                                        </div>
                                    )}
                                </WarningCard>
                            )}
                        </>
                    )}
                </>
            )}
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.CLOSE_ACCOUNT}>
        <CloseAccountModal {...props} />
    </SigningContextProvider>
);
